import { Layer } from './layer';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { XYZ, WMTS } from 'ol/source';
import * as olProj from 'ol/proj';
import * as olExtent from 'ol/extent';
import TileLayer from 'ol/layer/Tile';
import WebGLTileLayer from 'ol/layer/WebGLTile.js';
import { Capacitor } from '@capacitor/core';

export class WmtsLayer extends Layer {

  declare id: string;
  metadataId: string;
  layerName: string;
  format: string;
  base: boolean;
  singleTile: boolean;
  minScaleDenominator: number;
  maxScaleDenominator: number;
  origin: Array<number>;
  projectionCode: string;

  sublayers: Object[] = new Array();
  declare url: string;

  tilegridExtent: Array<number>;
  tilegridLevels: Array<number>;
  themes: string[] = new Array();
  declare hasLegend: boolean;
  notilesUrl: string;
  clipHoleExtent: Array<number>;
  tileSize: number;

  hasWebGlSupport: boolean = false;

  constructor(
    id: string,
    metadataId: string,
    layerName: string,
    label: string,
    type: string,
    url: string,
    format: string,
    base: boolean,
    singleTile: boolean,
    tilegridExtent: Array<number>,
    tilegridLevels: Array<number>,
    projectionCode: string,
    minScaleDenominator?: number,
    maxScaleDenominator?: number,
    origin?: Array<number>,
    themes?: string[],
    hasLegend?: boolean,
    notilesUrl?: string,
    clipHoleExtent?: Array<number>,
    tileSize?: number,
    hasWebGlSupport?: boolean
  ) {

    super(id, label, type);
    if (metadataId) {
      this.metadataId = metadataId;
    }
    if (layerName) {
      this.layerName = layerName;
    }
    if (format) {
      this.format = format;
    }
    if (base) {
      this.base = base;
    }
    if (singleTile) {
      this.singleTile = singleTile;
    }
    this.projectionCode = projectionCode;

    this.tilegridExtent = tilegridExtent || olProj.get(this.projectionCode).getExtent();
    this.tilegridLevels = tilegridLevels || undefined;

    if (url) {
      //console.log('url', url);
      // this.url = this.formatUrl(url);
      this.url = url;
    }
    if (minScaleDenominator) {
      this.minScaleDenominator = minScaleDenominator;
    }
    if (maxScaleDenominator) {
      this.maxScaleDenominator = maxScaleDenominator;
    }
    if (origin) {
      this.origin = origin;
    }
    if (themes) {
      this.themes = themes;
    }
    if (hasLegend) {
      this.hasLegend = hasLegend;
    }
    if (notilesUrl) {
      this.notilesUrl = notilesUrl;
    }
    if (clipHoleExtent) {
      this.clipHoleExtent = clipHoleExtent;
    }
    if (tileSize) {
      this.tileSize = tileSize;
    }
    if (hasWebGlSupport) {
      this.hasWebGlSupport = hasWebGlSupport;
    }
  }

  toOlLayer() {
    let chosenProjectionWithCustomExtent = olProj.get(this.projectionCode);
    let originalExtent = chosenProjectionWithCustomExtent.getExtent();
    let serviceParams: any = {};
    serviceParams.matrixSet = this.projectionCode.substring(this.projectionCode.indexOf(":") + 1);
    serviceParams.format = "image/" + this.format;
    serviceParams.tileSize = (this.tileSize) ? this.tileSize : 256;
    // this.setWmtsProjectionParams(serviceParams, geoXportViewer_baselayer_proj.toUpperCase(), originalExtent);

    serviceParams.layer = this.layerName;
    serviceParams.tilegridExtent = this.tilegridExtent;
    this.setWmtsProjectionParams(serviceParams, this.projectionCode.toUpperCase(), originalExtent);
    serviceParams.url = this.url;


    serviceParams.tileGrid = new WMTSTileGrid({
      origin: serviceParams.origin,
      resolutions: serviceParams.resolutions,
      matrixIds: serviceParams.matrixIds,
      tileSize: serviceParams.tileSize,
      extent: serviceParams.tilegridExtent//,
    })

    let wmtsSource;
    if (serviceParams.url.indexOf("{") > -1) {
      wmtsSource = new XYZ({
        url: serviceParams.url,
        projection: chosenProjectionWithCustomExtent,
        crossOrigin: 'anonymous',
        // transition: 300,
        tileGrid: serviceParams.tileGrid
      });
      //testing geotifs  
      if (serviceParams.url.indexOf("geotiff") > -1) {
        wmtsSource.setTileLoadFunction((tileCoord, src) => {
          if (!tileCoord) return '';
          const z = tileCoord.tileCoord[0];
          const y = tileCoord.tileCoord[1];
          const x = tileCoord.tileCoord[2];
          tileCoord.getImage().src = `/assets/top25r_extr/geotiff/tiles/${z}/${y}/${x}.png`;
        });
      }

    } else {
      wmtsSource = new WMTS({
        requestEncoding: "REST",
        url: serviceParams.url,
        layer: serviceParams.layer,
        matrixSet: serviceParams.matrixSet,
        format: serviceParams.format,
        style: serviceParams.style,
        crossOrigin: 'anonymous',
        transition: 300,
        tileGrid: serviceParams.tileGrid
      });
    }

    const layerOptions: any = {
      source: wmtsSource,
      opacity: 1.0,

      preload: Infinity
    };
    if (this.minScaleDenominator !== undefined) {
      layerOptions.minResolution = this.getResolutionForScale(this.minScaleDenominator);
    }
    if (this.maxScaleDenominator !== undefined) {
      layerOptions.maxResolution = this.getResolutionForScale(this.maxScaleDenominator);
    }
    //console.log('WmtsLayer.isMobileOS()', WmtsLayer.isMobileOS());

    let tileLayer: TileLayer | WebGLTileLayer;
    // if (this.id === 'crossborder.notiles.overlay' || this.id === 'crossborder.notiles' || !Capacitor.isNativePlatform() || !this.hasWebGlSupport) {
    if (!WmtsLayer.isMobileOS() || !this.hasWebGlSupport) {
      tileLayer = new TileLayer(layerOptions);
    }
    else {
      tileLayer = new WebGLTileLayer(layerOptions);
    }
    //tileurl for 'no data' png
    if (this.notilesUrl) {
      wmtsSource.setTileUrlFunction(() => this.notilesUrl);
    }
    //overlay underneath crossborder
    if (this.id == 'crossborder.notiles.overlay') {
      if (document.body.classList.contains('ngi-theme-dark')) {
        wmtsSource.setTileUrlFunction(() => './assets/images/darktile.png');
      }
      else {
        wmtsSource.setTileUrlFunction(() => './assets/images/lighttile.png');
      }
    }

    tileLayer.set('customId', this.id)
    return tileLayer
  }

  static isMobileOS(): boolean {
    try {
      // 'android', 'ios', 'web'
      if (/^(android|ios)$/.test(Capacitor.getPlatform())) return true;
      return /Mobi|android|webos|iphone|ipad|opera mini/i.test(navigator.userAgent);
    } catch (error) { }
    return false;
  }


  private formatUrl(url: string): string {
    //"https://www.ngi.be/tiles/wmts//cartoweb/1.0.0/topo/default_bw/3857/latest/{z}/{y}/{x}.png"
    //"https://www.ngi.be/tiles/wmts//cartoweb/1.0.0/topo/default_bw/3857/latest/{z}/{y}/{x}.png"
    let zyxParamsIndex: number = url.indexOf("{");
    return url.substr(0, zyxParamsIndex) + "{z}/{y}/{x}." + this.format;
  }

  private setWmtsProjectionParams(serviceParams: any, projection: string, originalExtent: any) {

    switch (projection) {
      case "EPSG:3857":
        {
          var size = olExtent.getWidth(originalExtent) / serviceParams.tileSize;
          serviceParams.resolutions = [];
          serviceParams.matrixIds = [];

          let minTileGridLevel = this.tilegridLevels ? this.tilegridLevels[0] : 7
          let maxTileGridLevel = this.tilegridLevels ? this.tilegridLevels[1] : 18

          for (var z = minTileGridLevel; z < maxTileGridLevel; z++) {
            serviceParams.resolutions[z] = (size / Math.pow(2, z));
            serviceParams.matrixIds[z] = z;
          }
          serviceParams.origin = olExtent.getTopLeft(originalExtent);

          break;
        }
      case "EPSG:3812":
        {
          //var size = olExtent.getWidth(originalExtent) / serviceParams.tileSize;
          // if (this.tilegridLevels) {
          //   serviceParams.resolutions = [];
          //   serviceParams.matrixIds = [];
          //   for (var z = this.tilegridLevels[0]; z < this.tilegridLevels[1]; ++z) {
          //     serviceParams.resolutions[z] = size / Math.pow(2, z);
          //     serviceParams.matrixIds[z] = z;
          //     serviceParams.origin = this.origin || olExtent.getTopLeft(originalExtent);
          //   }
          // } else {

          serviceParams.resolutions = [
            1058.3354500042335,
            529.1677250021168,
            211.667090000847,
            132.2919312505292,
            66.1459656252646,
            26.458386250105836,
            13.229193125052918,
            6.614596562526459,
            2.6458386250105836,
            1.3229193125052918,
            0.661459656252646,
            0.330729828126323,
            0.1653649140631615
          ];
          serviceParams.matrixIds = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
          serviceParams.origin = this.origin || olExtent.getTopLeft(originalExtent);
          if (this.tilegridLevels) {
            serviceParams.matrixIds = serviceParams.matrixIds.slice(this.tilegridLevels[0], this.tilegridLevels[1]);
            serviceParams.resolutions = serviceParams.resolutions.slice(this.tilegridLevels[0], this.tilegridLevels[1]);
          }
          // }


          break;
        }
      default:
        {
          throw new Error(projection + ' is not a supported projection');
        }
    }
  }

  updateHasWebGlSupport(hasWebGlSupport: boolean) {
    this.hasWebGlSupport = hasWebGlSupport
  }
}
